import React, { useState, useContext, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Collapse,
  Popover,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Send,
  Map as MapIcon,
  HeadsetMic,
  DirectionsRun,
  EventNote,
  TravelExplore,
  BusinessCenter,
  ReceiptLong,
  CreditScore,
  Security,
  Person,
  LocalHospital,
  Group,
  Commute,
  DirectionsCar,
  AccountBalanceWallet,
  Business,
  BarChart,
  SettingsApplications,
  LocalTaxi,
  TrackChanges,
  MoneyRounded,
  AssignmentTurnedIn
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import logo from '../../img11.jpg';
import { RoleContext } from '../RoleContext';
import { UserContext } from '../UserContext';
import { MenuWidthContext } from '../MenuContext';

const Menu = () => {
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  const userdata = useContext(UserContext);
  const user = JSON.parse(userdata);
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);
  const userType = typeof window !== 'undefined' ? localStorage.getItem('userType') : null;
  const isCompanyUser = userType === 'ambulance_company';

  const [isOpen, setIsOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { setMenuWidth } = useContext(MenuWidthContext);

  const drawerWidth = isMobile ? 70 : (isOpen ? 250 : 70);




  useEffect(() => {

    const checkMobile = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      setIsOpen(!mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setMenuWidth(drawerWidth);
  }, [drawerWidth, setMenuWidth]);

  const handlePopoverOpen = (event, submenu) => {
    if (isMobile || !isOpen) {
      setAnchorEl(event.currentTarget);
      setActiveSubmenu(submenu);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setActiveSubmenu(null);
  };

  const handleSubmenuToggle = (event, submenu) => {
    if (isMobile || !isOpen) {
      handlePopoverOpen(event, submenu);
    } else {
      setOpenSubmenus(prev => ({
        ...prev,
        [submenu]: !prev[submenu]
      }));
    }
  };

  const handleToggle = () => {
    if (!isMobile) {
      setIsOpen(!isOpen);
    }
  };

  // Complete menu items configuration
  const menuItems = [
    // Dedicated dashboard for ambulance companies
    ...(isCompanyUser ? [{
      id: 'ambulance_company_dashboard',
      permission: 'view_dashboard',
      icon: <DashboardIcon />,
      label: 'Dashboard',
      path: '/dashboardForAmbulanceComapny'
    }] : []),
    {
      id: 'dashboard',
      permission: 'view_dashboard',
      icon: <DashboardIcon />,
      label: 'Dashboard',
      path: '/dashboard'
    },
    {
      id: 'liveTracking',
      permission: 'view_dashboard',
      icon: <TrackChanges />,
      label: 'Live Emergency Tracking',
      path: '/liveTracking'
    },
    {
      id: 'passengers',
      permissions: ['list_passengers', 'list_patients'],
      icon: <Group />,
      label: 'Riders',
      subItems: [
        {
          permission: 'list_passengers',
          icon: <Group />,
          label: 'Passengers',
          path: '/passengers'
        },
        {
          permission: 'list_patients',
          icon: <Group />,
          label: 'Patients',
          path: '/patients'
        }
      ]
    },
    {
      id: 'drivers',
      permissions: ['list_drivers', 'list_active_on_map', 'list_active_drivers'],
      icon: <Person />,
      label: 'Drivers',
      subItems: [
        {
          permission: 'list_drivers',
          icon: <PeopleIcon />,
          label: 'All Drivers',
          path: '/drivers'
        },
        {
          permission: 'list_active_drivers',
          icon: <LocalTaxi />,
          label: 'Active Drivers',
          path: '/activeDrivers'
        },
        {
          permission: 'list_active_on_map',
          icon: <MapIcon />,
          label: 'Active on Map ',
          path: '/driversMap'
        }
      ]
    },
    {
      id: 'active_drivers',
      permissions: ['list_active_on_map'],
      icon: <Person />,
      label: 'Dispatchers',
      subItems: [
        {
          permission: 'list_active_on_map',
          icon: <Send />,
          label: 'Broadcast Message',
          path: '/broadcastMessage'
        },

        {
          permission: 'list_active_on_map',
          icon: <HeadsetMic />,
          label: 'Dispatcher',
          path: '/dispatch'
        }
      ]
    },
    {
      id: 'vehicles',
      permission: 'list_vehicles',
      icon: <DirectionsCar />,
      label: 'Vehicles',
      path: '/vehicleList'
    },
    {
      id: 'trips',
      permissions: ['list_all_trips', 'list_active_trips', 'list_booked_trips'],
      icon: <Commute />,
      label: 'Trips',
      subItems: [
        {
          permission: 'list_active_trips',
          icon: <DirectionsRun />,
          label: 'Active Trips',
          path: isCompanyUser ? '/companyActiveTrips' : '/activeTrips'
        },
        ...(!isCompanyUser ? [{
          permission: 'list_active_trips',
          icon: <AssignmentTurnedIn />,
          label: 'Accepted Trips',
          path: '/acceptedTrips'
        }] : []),
        {
          permission: 'list_booked_trips',
          icon: <EventNote />,
          label: 'Booked Trips',
          path: '/reservations'
        },
        {
          permission: 'list_booked_trips',
          icon: <TravelExplore />,
          label: 'All Trips',
          path: isCompanyUser ? '/companyTrips' : '/trips'
        }
      ]
    },
    {
      id: 'financials',
      permissions: ['list_drivers_credits', 'list_transactions'],
      icon: <AccountBalanceWallet />,
      label: 'Financials',
      subItems: [
        {
          permission: 'list_drivers_credits',
          icon: <ReceiptLong />,
          label: 'Transactions',
          path: isCompanyUser ? '/companyTransactions' : '/transactionsList'
        },
        {
          permission: 'view_deleted_trips',
          icon: <ReceiptLong />,
          label: 'Deleted Trips (Audit)',
          path: '/deletedTrips'
        },
        {
          permission: 'list_drivers_credits',
          icon: <BusinessCenter />,
          label: 'Corporate Transactions',
          path: '/corporateTransactions'
        },
        {
          permission: 'list_drivers_credits',
          icon: <CreditScore />,
          label: 'Drivers Credit',
          path: '/creditsList'
        }
      ]
    },
    {
      id: 'accounts',
      permissions: ['list_company', 'list_subscriber', 'add_agent'],
      icon: <Business />,
      label: 'Accounts',
      subItems: [
        {
          permission: 'list_company',
          icon: <BusinessCenter />,
          label: 'Corporate Account',
          path: '/corporateAccount'
        },
        {
          permission: 'add_agent',
          icon: <Group />,
          label: 'Agents',
          path: '/agent'
        }
      ]
    },
    {
      id: 'userManagement',
      permissions: ['list_users', 'list_role', 'list_permissions'],
      icon: <PeopleIcon />,
      label: 'User Management',
      subItems: [
        {
          permission: 'list_users',
          icon: <Person />,
          label: 'Users',
          path: '/users'
        },
        {
          permission: 'list_roles',
          icon: <Security />,
          label: 'Roles',
          path: '/roles'
        }
      ]
    },
    {
      id: 'reports',
      permission: 'view_dashboard',
      icon: <BarChart />,
      label: 'Reports',
      path: '/report'
    },
    {
      id: 'lookups',
      permission: 'list_lookups',
      icon: <SettingsApplications />,
      label: 'Lookups ',
      subItems: [
        {
          permission: 'list_lookups',
          icon: <SettingsApplications />,
          label: 'Vehicle Types',
          path: '/vehicleTypes'
        },
        {
          permission: 'list_lookups',
          icon: <MoneyRounded />,
          label: 'Distance Pricing',
          path: '/distance-pricing'
        },
        {
          permission: 'list_lookups',
          icon: <DirectionsCar />,
          label: 'Approach Distance',
          path: '/approach-distance-config'
        },
        {
          permission: 'list_lookups',
          icon: <SettingsApplications />,
          label: 'Configuration',
          path: '/configuration'
        }
      ]
    },
    {
      id: 'ambulanceLookup',
      permissions: [
        'list_hospitals',
        'add_hospital',
        'update_hospital',
        'delete_hospital',
        'list_specialty',
        'add_specialty',
        'update_specialty',
        'delete_specialty',
        'assign_specialty_to_hospital',
        'remove_specialty_from_hospital',
        'view_hospital_specialties',
        'view_specialty_hospitals',
        'update_hospital_specialties'
      ],
      icon: <LocalHospital />,
      label: 'Ambulance Lookup',
      subItems: [
        {
          permission: 'list_hospitals',
          icon: <LocalHospital />,
          label: 'Ambulance Lookup',
          path: '/ambulance_lookup'
        }
      ]
    },
    // {
    //   id: 'Pending Ambulance Companies',
    //   permission: 'list_ambulance_company',
    //   icon: <LocalHospital />,
    //   label: 'Pending Ambulance Companies',
    //   path: '/admin/pending-ambulance-companies'
    // },
  ];

  const renderMenuItem = ({ id, permission, permissions, icon, label, path, subItems }) => {
    // First check if user has permission for the parent menu
    let hasPermission = permission
      ? role?.some(e => e.perm_name === permission)
      : permissions
        ? role?.some(e => permissions.some(perm => e.perm_name === perm))
        : false;

    // Ensure ambulance company dashboard is visible for company users
    if (isCompanyUser && id === 'ambulance_company_dashboard') {
      hasPermission = true;
    }

    // Allow ambulance company users to see Vehicles and Drivers even if explicit permissions are not granted
    if (isCompanyUser && (id === 'vehicles' || id === 'drivers')) {
      hasPermission = true;
    }

    if (!hasPermission) return null;

    // If there are subItems, filter them based on permissions
    let filteredSubItems = subItems?.filter(subItem =>
      role?.some(e => e.perm_name === subItem.permission)
    );


    // If there are no accessible subItems and no direct path, don't render the menu
    if (subItems && (!filteredSubItems || filteredSubItems.length === 0)) {
      return null;
    }

    const MenuItemContent = (
      <Box key={id}>
        <ListItem
          component={filteredSubItems ? 'div' : Link}
          to={path}
          onClick={filteredSubItems ? (e) => handleSubmenuToggle(e, id) : undefined}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
            },
            color: 'rgba(255,255,255,0.8)',
            transition: 'all 0.3s ease',
            minHeight: 48,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>{icon}</ListItemIcon>
          {!isMobile && isOpen && (
            <>
              <ListItemText primary={label} />
              {filteredSubItems && (
                openSubmenus[id] ? <ExpandLess /> : <ExpandMore />
              )}
            </>
          )}
        </ListItem>

        {!isMobile && isOpen && filteredSubItems && (
          <Collapse in={openSubmenus[id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {filteredSubItems.map((subItem, index) => (
                <ListItem
                  key={index}
                  component={Link}
                  to={subItem.path}
                  sx={{
                    pl: 4,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                    },
                    color: 'rgba(255,255,255,0.7)',
                    minHeight: 40,
                  }}
                >
                  <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                    {subItem.icon}
                  </ListItemIcon>
                  <ListItemText primary={subItem.label} />
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </Box>
    );

    return (isMobile || !isOpen) ? (
      <Tooltip title={label} key={id} placement="right">
        {MenuItemContent}
      </Tooltip>
    ) : MenuItemContent;
  };

  const renderSubmenuPopover = () => {
    const open = Boolean(anchorEl);
    const activeMenu = menuItems.find(item => item.id === activeSubmenu);
    // Filter submenu items based on permissions
    const filteredSubItems = activeMenu?.subItems?.filter(subItem =>
      role?.some(e => e.perm_name === subItem.permission)
    );

    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#191919',
            color: '#fff',
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
          }
        }}
      >
        {filteredSubItems && filteredSubItems.length > 0 && (
          <List>
            {filteredSubItems.map((subItem, index) => (
              <ListItem
                key={index}
                component={Link}
                to={subItem.path}
                onClick={handlePopoverClose}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                  },
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  {subItem.icon}
                </ListItemIcon>
                <ListItemText primary={subItem.label} />
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#000',
          color: 'white',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          backgroundColor: '#191919',
          minHeight: 64,
        }}
      >
        {!isMobile && isOpen && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={logo}
              alt="ለAmbulance Logo"
              style={{
                height: 40,
                marginRight: 10,
                borderRadius: '50%',
              }}
            />
            <Typography variant="h6" sx={{ color: 'white' }}>ለAmbulance</Typography>
          </Box>
        )}
        <IconButton
          onClick={handleToggle}
          sx={{
            color: 'white',
            ml: isMobile ? 0 : 'auto',
          }}
        >
          {isOpen && !isMobile ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <List sx={{ pt: 0 }}>
        {menuItems.map(item => renderMenuItem(item))}
      </List>

      {renderSubmenuPopover()}
    </Drawer>
  );
};
export default Menu;